import { Box, Button, Container, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FileRejection, useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { DocumentFilters } from "../features/documents/components/DocumentFilters";
import { DocumentList } from "../features/documents/components/DocumentList";
import { UploadStatusWindow } from "../features/documentUploads/components/UploadStatusWindow";
import { ACCEPTED_FILE_TYPES } from "../features/documentUploads/constants/uploadConstants";
import {
  uploadStart,
  setUploadState,
} from "../features/documentUploads/store/documentUploadsSlice";
import { RootState } from "../store/rootReducer";
import { MAX_FILE_SIZE } from "@stratify/shared";

export const DocumentsPage: React.FC = () => {
  const [isFileDragging, setIsFileDragging] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        setIsFileDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      // Only reset if leaving the window
      if (e.relatedTarget === null) {
        setIsFileDragging(false);
      }
    };

    const handleDrop = () => {
      setIsFileDragging(false);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  const uploads = useSelector(
    (state: RootState) => state.documentUploads.uploads
  );
  const isCollapsed = useSelector(
    (state: RootState) => state.documentUploads.isCollapsed
  );

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      await Promise.all(
        acceptedFiles.map((file) => {
          const id = uuidv4();
          return dispatch(uploadStart({ id, file }));
        })
      );
    },
    [dispatch]
  );

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      fileRejections.forEach((rejection) => {
        const isTooLarge = rejection.errors.some(
          (err: { code: string }) => err.code === "file-too-large"
        );
        if (isTooLarge) {
          const id = uuidv4();
          dispatch(
            setUploadState({
              id,
              state: {
                id,
                fileName: rejection.file.name,
                progress: 0,
                status: "error",
                errorMessage: `File is too large (Max 25MB)`,
                timestamp: Date.now(),
              },
            })
          );
        }
      });
    },
    [dispatch]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    noClick: true, // Prevent click-to-upload on the entire page
  });

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: 2,
        minHeight: "calc(100vh - 64px)", // Adjust based on your top bar height
        display: "flex",
        flexDirection: "column",
      }}
      {...getRootProps()}
    >
      <Box
        sx={{
          flex: 1,
          position: "relative",
        }}
      >
        {(isDragActive || isFileDragging) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: 3,
              borderStyle: "dashed",
              borderColor: "primary.main",
              borderRadius: 1,
              zIndex: 1000,
            }}
          >
            <CloudUploadIcon
              sx={{ fontSize: 64, mb: 2, color: "primary.main" }}
            />
            <Typography variant="h5" color="primary" gutterBottom>
              Drop PDF files here to upload
            </Typography>
            <Typography variant="body1" color="text.secondary">
              (Max 25MB)
            </Typography>
          </Box>
        )}
        <input {...getInputProps()} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              id="document-page-title"
              test-id="document-page-title"
              variant="h4"
              component="h1"
              gutterBottom
            >
              Documents
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Drag and drop PDF files to upload.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => {
              const fileInput = document.querySelector(
                'input[type="file"]'
              ) as HTMLInputElement;
              if (fileInput) {
                fileInput.click();
              }
            }}
          >
            Upload Files
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <UploadStatusWindow uploads={uploads} isCollapsed={isCollapsed} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <DocumentFilters />
        </Box>

        <DocumentList />
      </Box>
    </Container>
  );
};
